import type { LayerKind, LayerProperties } from './layer';
import { defaultLayerProperties, defaultTextStyle } from './layer';

export type TransitionType = 'none' | 'crossDissolve' | 'dipToBlack' | 'wipe';

export interface Transition {
  id: string;
  type: TransitionType;
  duration: number; // seconds
}

export interface TimelineMarker {
  id: string;
  time: number;
  label: string;
  color: string;
}

export interface MediaAsset {
  id: string;
  name: string;
  kind: 'video' | 'audio' | 'image' | 'adjustment' | 'solid';
  /** Object URL or blob URL — local only, never uploaded. Empty for synthetic assets. */
  url: string;
  mimeType: string;
  duration: number;
  width: number;
  height: number;
  /** Interpreted / conform frame rate (seconds ↔ frames). */
  fps: number;
  /**
   * Native / assumed source frame rate at import.
   * Used by Interpret Footage; falls back to `fps` when omitted.
   */
  nativeFps?: number;
  sizeBytes: number;
  createdAt: number;
  /** Solid layer fill color (#rrggbb) */
  solidColor?: string;
}

export interface Clip {
  id: string;
  trackId: string;
  assetId: string | null; // null for text/solid/adjustment
  kind: LayerKind;
  name: string;
  /** Timeline start in seconds */
  start: number;
  /** Duration on timeline in seconds */
  duration: number;
  /** In-point in source media */
  inPoint: number;
  /** Out-point in source media */
  outPoint: number;
  properties: LayerProperties;
  transitionIn: Transition | null;
  transitionOut: Transition | null;
  locked: boolean;
  muted: boolean;
  /**
   * When set, clips sharing this id move/trim together (linked A/V).
   * Null/undefined = independent.
   */
  linkGroupId?: string | null;
}

export type TrackKind = 'video' | 'audio' | 'subtitle';
export type AudioTrackChannels = 'mono' | 'stereo';

export interface Track {
  id: string;
  name: string;
  kind: TrackKind;
  height: number;
  muted: boolean;
  solo: boolean;
  locked: boolean;
  visible: boolean;
  /** Audio tracks only — defaults to stereo when omitted. */
  channels?: AudioTrackChannels;
}

export interface CompositionSettings {
  width: number;
  height: number;
  frameRate: number;
  sampleRate: number;
  duration: number;
  backgroundColor: string;
  pixelAspect: number;
}

/** One editable timeline / composition inside a project. */
export interface Composition {
  id: string;
  name: string;
  settings: CompositionSettings;
  tracks: Track[];
  clips: Clip[];
  markers: TimelineMarker[];
}

export interface Project {
  id: string;
  name: string;
  version: number;
  createdAt: number;
  updatedAt: number;
  /**
   * Active composition mirror (kept in sync for existing UI / sync code).
   * Canonical copies live in `compositions`.
   */
  settings: CompositionSettings;
  tracks: Track[];
  clips: Clip[];
  assets: MediaAsset[];
  markers: TimelineMarker[];
  /** All compositions / timelines in this project */
  compositions: Composition[];
  activeCompositionId: string;
}

export function defaultCompositionSettings(): CompositionSettings {
  return {
    width: 1920,
    height: 1080,
    frameRate: 30,
    sampleRate: 48000,
    duration: 60,
    backgroundColor: '#000000',
    pixelAspect: 1,
  };
}

export function createDefaultTracks(): Track[] {
  return [
    { id: crypto.randomUUID(), name: 'V2', kind: 'video', height: 56, muted: false, solo: false, locked: false, visible: true },
    { id: crypto.randomUUID(), name: 'V1', kind: 'video', height: 56, muted: false, solo: false, locked: false, visible: true },
    { id: crypto.randomUUID(), name: 'A1', kind: 'audio', height: 40, muted: false, solo: false, locked: false, visible: true, channels: 'stereo' },
    { id: crypto.randomUUID(), name: 'A2', kind: 'audio', height: 40, muted: false, solo: false, locked: false, visible: true, channels: 'stereo' },
  ];
}

export function suggestTrackName(tracks: Track[], kind: 'video' | 'audio'): string {
  const prefix = kind === 'video' ? 'V' : 'A';
  let max = 0;
  for (const t of tracks) {
    if (t.kind !== kind) continue;
    const m = new RegExp(`^${prefix}(\\d+)$`, 'i').exec(t.name.trim());
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `${prefix}${max + 1}`;
}

export function createTrack(
  kind: TrackKind,
  name: string,
  opts?: { channels?: AudioTrackChannels }
): Track {
  const base: Track = {
    id: crypto.randomUUID(),
    name: name.trim() || (kind === 'audio' ? 'A1' : 'V1'),
    kind,
    height: kind === 'audio' ? 40 : 56,
    muted: false,
    solo: false,
    locked: false,
    visible: true,
  };
  if (kind === 'audio') {
    base.channels = opts?.channels ?? 'stereo';
  }
  return base;
}

export function createComposition(name = 'Comp 1'): Composition {
  return {
    id: crypto.randomUUID(),
    name,
    settings: defaultCompositionSettings(),
    tracks: createDefaultTracks(),
    clips: [],
    markers: [],
  };
}

/** Flush flat active fields into compositions[], then mirror active back out. */
export function flushActiveComposition(project: Project): Project {
  const ensured = ensureCompositions(project);
  const id = ensured.activeCompositionId;
  const compositions = ensured.compositions.map((c) =>
    c.id === id
      ? {
          ...c,
          settings: ensured.settings,
          tracks: ensured.tracks,
          clips: ensured.clips,
          markers: ensured.markers ?? [],
        }
      : c
  );
  return {
    ...ensured,
    compositions,
    activeCompositionId: id,
    markers: ensured.markers ?? [],
  };
}

/** Migrate legacy projects (no compositions) and repair missing active id. */
export function ensureCompositions(project: Project): Project {
  if (project.compositions?.length) {
    const active =
      project.compositions.find((c) => c.id === project.activeCompositionId) ??
      project.compositions[0];
    // Keep the flat working copy authoritative; only repair id / empties.
    return {
      ...project,
      activeCompositionId: active.id,
      compositions: project.compositions,
      settings: project.settings ?? active.settings,
      tracks: project.tracks?.length ? project.tracks : active.tracks,
      clips: project.clips ?? active.clips,
      markers: project.markers ?? active.markers ?? [],
    };
  }

  const comp: Composition = {
    id: crypto.randomUUID(),
    name: 'Comp 1',
    settings: project.settings ?? defaultCompositionSettings(),
    tracks: project.tracks?.length ? project.tracks : createDefaultTracks(),
    clips: project.clips ?? [],
    markers: project.markers ?? [],
  };
  return {
    ...project,
    compositions: [comp],
    activeCompositionId: comp.id,
    settings: comp.settings,
    tracks: comp.tracks,
    clips: comp.clips,
    markers: comp.markers,
  };
}

export function createEmptyProject(name = 'Untitled Project'): Project {
  const now = Date.now();
  const comp = createComposition('Comp 1');
  return {
    id: crypto.randomUUID(),
    name,
    version: 1,
    createdAt: now,
    updatedAt: now,
    settings: comp.settings,
    tracks: comp.tracks,
    clips: comp.clips,
    assets: [],
    markers: comp.markers,
    compositions: [comp],
    activeCompositionId: comp.id,
  };
}

export function createMarker(time: number, label?: string): TimelineMarker {
  const colors = ['#E8A33D', '#4ADE80', '#60A5FA', '#F472B6', '#A78BFA'];
  return {
    id: crypto.randomUUID(),
    time,
    label: label ?? `M${Math.floor(time)}`,
    color: colors[Math.floor(time * 10) % colors.length],
  };
}

export function createClip(partial: Partial<Clip> & Pick<Clip, 'trackId' | 'kind' | 'name' | 'start' | 'duration'>): Clip {
  const props = defaultLayerProperties();
  if (partial.kind === 'text') {
    props.text = defaultTextStyle();
  }
  return {
    id: crypto.randomUUID(),
    assetId: null,
    inPoint: 0,
    outPoint: partial.duration,
    properties: props,
    transitionIn: null,
    transitionOut: null,
    locked: false,
    muted: false,
    linkGroupId: null,
    ...partial,
  };
}

export function createCrossDissolve(duration = 0.5): Transition {
  return {
    id: crypto.randomUUID(),
    type: 'crossDissolve',
    duration,
  };
}
